import { api, invalidateItem, invalidateList, itemTags, listTags } from "../api";

// Lookup lists (countries, markets, ...) change rarely and are read all over
// the app, so keep them cached for a while after the last subscriber goes.
const LOOKUP_CACHE_SECONDS = 600;

export const userApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Customers
    getCustomers: builder.query({
      query: (params) => ({
        url: `customers`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Customer"),
    }),
    getCustomer: builder.query({
      query: (id) => ({
        url: `customers/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Customer"),
    }),
    updateCustomer: builder.mutation({
      query: ({ id, data }) => ({
        url: `customers/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateItem("Customer"),
    }),
    deleteCustomer: builder.mutation({
      query: (id) => ({
        url: `customers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateItem("Customer"),
    }),

    // Shoppers
    getShoppers: builder.query({
      query: (params) => ({
        url: `shoppers`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Shopper"),
    }),
    getShopper: builder.query({
      query: (id) => ({
        url: `shoppers/admin/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Shopper"),
    }),
    updateShopper: builder.mutation({
      query: ({ id, data }) => ({
        url: `shoppers/admin/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateItem("Shopper"),
    }),
    deleteShopper: builder.mutation({
      query: (id) => ({
        url: `shoppers/admin/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateItem("Shopper"),
    }),

    // Areas
    getAreas: builder.query({
      query: (params) => ({
        url: `areas`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Area"),
      keepUnusedDataFor: LOOKUP_CACHE_SECONDS,
    }),
    getArea: builder.query({
      query: (id) => ({
        url: `areas/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Area"),
    }),
    createArea: builder.mutation({
      query: (data) => ({
        url: `areas`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Area"),
    }),
    updateArea: builder.mutation({
      query: ({ id, data }) => ({
        url: `areas/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateItem("Area"),
    }),
    deleteArea: builder.mutation({
      query: (id) => ({
        url: `areas/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateItem("Area"),
    }),

    // Categories
    getCategories: builder.query({
      query: (params) => ({
        url: `categories`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Category"),
      keepUnusedDataFor: LOOKUP_CACHE_SECONDS,
    }),
    getCategory: builder.query({
      query: (id) => ({
        url: `categories/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Category"),
    }),
    createCategory: builder.mutation({
      query: (data) => ({
        url: `categories`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Category"),
    }),
    updateCategory: builder.mutation({
      query: ({ id, data }) => ({
        url: `categories/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateItem("Category"),
    }),
    deleteCategory: builder.mutation({
      query: (id) => ({
        url: `categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateItem("Category"),
    }),

    // Products
    getProducts: builder.query({
      query: (params) => ({
        url: `products/admin`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Product"),
    }),
    getProduct: builder.query({
      query: (id) => ({
        url: `products/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Product"),
    }),
    createProduct: builder.mutation({
      query: (data) => ({
        url: `products`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Product", "PriceByMarket"),
    }),
    updateProduct: builder.mutation({
      query: ({ id, data }) => ({
        url: `products/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("Product")(result, error, arg), { type: "PriceByMarket", id: "LIST" }],
    }),
    deleteProduct: builder.mutation({
      query: (id) => ({
        url: `products/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("Product")(result, error, arg), { type: "PriceByMarket", id: "LIST" }],
    }),

    // Markets
    getMarkets: builder.query({
      query: (params) => ({
        url: `markets`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Market"),
      keepUnusedDataFor: LOOKUP_CACHE_SECONDS,
    }),
    getMarket: builder.query({
      query: (id) => ({
        url: `markets/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Market"),
    }),
    createMarket: builder.mutation({
      query: (data) => ({
        url: `markets`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Market"),
    }),
    // Areas embed their market, so market edits refresh areas too.
    updateMarket: builder.mutation({
      query: ({ id, data }) => ({
        url: `markets/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("Market")(result, error, arg), { type: "Area", id: "LIST" }],
    }),
    deleteMarket: builder.mutation({
      query: (id) => ({
        url: `markets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("Market")(result, error, arg), { type: "Area", id: "LIST" }],
    }),

    // Supports
    getSupports: builder.query({
      query: (params) => ({
        url: `supports`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Support"),
      keepUnusedDataFor: LOOKUP_CACHE_SECONDS,
    }),
    getSupport: builder.query({
      query: (id) => ({
        url: `supports/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Support"),
    }),
    createSupport: builder.mutation({
      query: (data) => ({
        url: `supports`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Support"),
    }),
    updateSupport: builder.mutation({
      query: ({ id, data }) => ({
        url: `supports/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateItem("Support"),
    }),
    deleteSupport: builder.mutation({
      query: (id) => ({
        url: `supports/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateItem("Support"),
    }),

    // Countries
    getCountries: builder.query({
      query: (params) => ({
        url: `countries`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Country"),
      keepUnusedDataFor: LOOKUP_CACHE_SECONDS,
    }),
    getCountry: builder.query({
      query: (id) => ({
        url: `countries/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Country"),
    }),
    createCountry: builder.mutation({
      query: (data) => ({
        url: `countries`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Country"),
    }),
    // Areas and markets embed country details, so refresh them too.
    updateCountry: builder.mutation({
      query: ({ id, data }) => ({
        url: `countries/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        ...invalidateItem("Country")(result, error, arg),
        { type: "Area", id: "LIST" },
        { type: "Market", id: "LIST" },
      ],
    }),
    deleteCountry: builder.mutation({
      query: (id) => ({
        url: `countries/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateItem("Country"),
    }),

    // Units of measure
    getUoms: builder.query({
      query: (params) => ({
        url: `uoms`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Uom"),
      keepUnusedDataFor: LOOKUP_CACHE_SECONDS,
    }),
    getUom: builder.query({
      query: (id) => ({
        url: `uoms/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Uom"),
    }),
    createUom: builder.mutation({
      query: (data) => ({
        url: `uoms`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Uom"),
    }),
    updateUom: builder.mutation({
      query: ({ id, data }) => ({
        url: `uoms/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateItem("Uom"),
    }),
    deleteUom: builder.mutation({
      query: (id) => ({
        url: `uoms/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: invalidateItem("Uom"),
    }),

    // Banks
    getBanks: builder.query({
      query: () => ({
        url: "banks",
        method: "GET",
      }),
      providesTags: [{ type: "Bank", id: "LIST" }],
      keepUnusedDataFor: LOOKUP_CACHE_SECONDS,
    }),
    verifyAccountNumber: builder.mutation({
      query: (data) => ({
        url: "shoppers/verify-account-number",
        method: "POST",
        body: data,
      }),
    }),

    // Price by market
    getPriceByMarkets: builder.query({
      query: (params) => ({
        url: `price-by-markets`,
        method: "GET",
        params,
      }),
      providesTags: listTags("PriceByMarket"),
    }),
    getPriceByMarket: builder.query({
      query: (id) => ({
        url: `price-by-markets/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("PriceByMarket"),
    }),
    createPriceByMarket: builder.mutation({
      query: (data) => ({
        url: `price-by-markets`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("PriceByMarket", "Product"),
    }),
    updatePriceByMarket: builder.mutation({
      query: ({ id, data }) => ({
        url: `price-by-markets/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("PriceByMarket")(result, error, arg), { type: "Product", id: "LIST" }],
    }),
    deletePriceByMarket: builder.mutation({
      query: (id) => ({
        url: `price-by-markets/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("PriceByMarket")(result, error, arg), { type: "Product", id: "LIST" }],
    }),

    // Vendors
    getVendors: builder.query({
      query: (params) => ({
        url: `vendors`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Vendor"),
    }),
    getVendor: builder.query({
      query: (id) => ({
        url: `vendors/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Vendor"),
    }),
    createVendor: builder.mutation({
      query: (data) => ({
        url: "vendors/create",
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Vendor"),
    }),
    updateVendor: builder.mutation({
      query: ({ id, data }) => ({
        url: `vendors/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateItem("Vendor"),
    }),

    // Vouchers
    getVouchers: builder.query({
      query: (params) => ({
        url: `vouchers`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Voucher"),
    }),

    // Orders. Order changes move counts on the dashboard and can create or
    // settle transactions, so they invalidate Stats and Transaction as well.
    getOrders: builder.query({
      query: (params) => ({
        url: `orders/admin`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Order"),
    }),
    getOrder: builder.query({
      query: (id) => ({
        url: `orders/admin/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Order"),
    }),
    createOrder: builder.mutation({
      query: (data) => ({
        url: `orders/admin`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: invalidateList("Order", "Stats"),
    }),
    updateOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: `orders/admin/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        ...invalidateItem("Order")(result, error, arg),
        ...invalidateList("Transaction", "Stats")(),
      ],
    }),
    updateOrderStatus: builder.mutation({
      query: ({ id, data }) => ({
        url: `orders/admin/status/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [
        ...invalidateItem("Order")(result, error, arg),
        ...invalidateList("Transaction", "Stats", "Shopper", "Vendor")(),
      ],
    }),
    assignOrderShopper: builder.mutation({
      query: ({ id, data }) => ({
        url: `orders/admin/shopper/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("Order")(result, error, arg), ...invalidateList("Shopper", "Stats")()],
    }),
    unassignOrderShopper: builder.mutation({
      query: ({ id, data }) => ({
        url: `orders/admin/shopper/remove/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, arg) => [...invalidateItem("Order")(result, error, arg), ...invalidateList("Shopper", "Stats")()],
    }),
    deleteOrder: builder.mutation({
      query: (id) => ({
        url: `orders/admin/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [
        ...invalidateItem("Order")(result, error, arg),
        ...invalidateList("Transaction", "Stats")(),
      ],
    }),

    // Transactions
    getTransactions: builder.query({
      query: (params) => ({
        url: `transactions`,
        method: "GET",
        params,
      }),
      providesTags: listTags("Transaction"),
    }),
    getTransaction: builder.query({
      query: (id) => ({
        url: `transactions/${id}`,
        method: "GET",
      }),
      providesTags: itemTags("Transaction"),
    }),
    bulkUpdateOrderTransactionsStatus: builder.mutation({
      query: (data) => ({
        url: `transactions/status/bulk/orders`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateList("Transaction", "Order", "Stats"),
    }),
    bulkUpdateShopperTransactionsStatus: builder.mutation({
      query: (data) => ({
        url: `transactions/status/bulk/shoppers`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateList("Transaction", "Shopper", "Stats"),
    }),
    bulkUpdateVendorTransactionsStatus: builder.mutation({
      query: (data) => ({
        url: `transactions/status/bulk/vendors`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: invalidateList("Transaction", "Vendor", "Stats"),
    }),

    // Statistics
    getDashboardStats: builder.query({
      query: (params) => ({
        url: `statistics/dashboard`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Stats", id: "LIST" }],
    }),
    getOrderStatsByInterval: builder.query({
      query: (params) => ({
        url: `statistics/dashboard/orders`,
        method: "GET",
        params,
      }),
      providesTags: [{ type: "Stats", id: "LIST" }],
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useLazyGetCustomerQuery,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useGetShoppersQuery,
  useLazyGetShoppersQuery,
  useLazyGetShopperQuery,
  useUpdateShopperMutation,
  useDeleteShopperMutation,
  useGetAreasQuery,
  useLazyGetAreasQuery,
  useLazyGetAreaQuery,
  useCreateAreaMutation,
  useUpdateAreaMutation,
  useDeleteAreaMutation,
  useGetCategoriesQuery,
  useLazyGetCategoriesQuery,
  useLazyGetCategoryQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useLazyGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useGetMarketsQuery,
  useLazyGetMarketsQuery,
  useLazyGetMarketQuery,
  useCreateMarketMutation,
  useUpdateMarketMutation,
  useDeleteMarketMutation,
  useGetSupportsQuery,
  useLazyGetSupportsQuery,
  useLazyGetSupportQuery,
  useCreateSupportMutation,
  useUpdateSupportMutation,
  useDeleteSupportMutation,
  useGetCountriesQuery,
  useLazyGetCountriesQuery,
  useLazyGetCountryQuery,
  useCreateCountryMutation,
  useUpdateCountryMutation,
  useDeleteCountryMutation,
  useGetUomsQuery,
  useLazyGetUomsQuery,
  useLazyGetUomQuery,
  useCreateUomMutation,
  useUpdateUomMutation,
  useDeleteUomMutation,
  useGetBanksQuery,
  useLazyGetBanksQuery,
  useVerifyAccountNumberMutation,
  useLazyGetPriceByMarketsQuery,
  useLazyGetPriceByMarketQuery,
  useCreatePriceByMarketMutation,
  useUpdatePriceByMarketMutation,
  useDeletePriceByMarketMutation,
  useGetVendorsQuery,
  useLazyGetVendorsQuery,
  useLazyGetVendorQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useGetVouchersQuery,
  useLazyGetVouchersQuery,
  useGetOrdersQuery,
  useLazyGetOrdersQuery,
  useLazyGetOrderQuery,
  useCreateOrderMutation,
  useUpdateOrderMutation,
  useUpdateOrderStatusMutation,
  useAssignOrderShopperMutation,
  useUnassignOrderShopperMutation,
  useDeleteOrderMutation,
  useGetTransactionsQuery,
  useLazyGetTransactionsQuery,
  useLazyGetTransactionQuery,
  useBulkUpdateOrderTransactionsStatusMutation,
  useBulkUpdateShopperTransactionsStatusMutation,
  useBulkUpdateVendorTransactionsStatusMutation,
  useLazyGetDashboardStatsQuery,
  useLazyGetOrderStatsByIntervalQuery,
} = userApi;
